import type { ConfigValue, PipelineSourceField, PipelineStep, StepConfig, VisualPipeline } from './types';
import { FUNCTION_REGISTRY_MAP } from './functionRegistry';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when an identifier needs double-quoting (spaces, special chars, leading digit). */
const needsQuoting = (name: string): boolean => !/^[A-Za-z_]\w*$/.test(name);

/** Wrap a field/column name in double-quotes when it contains spaces or special characters.
 *  Internal double-quotes are escaped per SQL standard (doubled). */
export const quoteIdentifier = (name: string): string =>
  needsQuoting(name) ? `"${name.replace(/"/g, '""')}"` : name;

/** Strip surrounding double-quotes from a quoted identifier and unescape doubled quotes. */
export const unquoteIdentifier = (name: string): string => {
  const t = name.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1).replace(/""/g, '"');
  }
  return t;
};

const configValueToString = (cv: ConfigValue, sourceFields: PipelineSourceField[]): string => {
  if (cv.kind === 'field') {
    const sf = sourceFields.find((f) => f.id === cv.fieldId);
    const fieldName = sf?.sourceField ?? cv.fieldName;
    return quoteIdentifier(fieldName);
  }
  // String literals use single quotes (SQL standard).
  return `'${cv.value.replace(/'/g, "\\'")}'`;
};

const configValueToRaw = (cv: ConfigValue, sourceFields: PipelineSourceField[]): string => {
  if (cv.kind === 'field') {
    const sf = sourceFields.find((f) => f.id === cv.fieldId);
    const fieldName = sf?.sourceField ?? cv.fieldName;
    return quoteIdentifier(fieldName);
  }
  return cv.value;
};

// ---------------------------------------------------------------------------
// Serialize: VisualPipeline → expression string
// ---------------------------------------------------------------------------

export const serializePipeline = (pipeline: VisualPipeline): string => {
  const { sourceFields, steps } = pipeline;
  if (steps.length === 0) {
    // Direct mapping — just the source field name (quoted if it contains spaces)
    return sourceFields.length > 0 ? quoteIdentifier(sourceFields[0].sourceField) : '';
  }

  // Walk through steps in order. The first step receives the source field as input.
  // Each subsequent step wraps around the previous result.
  let expr = sourceFields.length > 0 ? quoteIdentifier(sourceFields[0].sourceField) : 'column_name';

  for (const step of steps) {
    expr = serializeStep(step, expr, sourceFields);
  }

  return expr;
};

const serializeStep = (
  step: PipelineStep,
  input: string,
  sourceFields: PipelineSourceField[]
): string => {
  const c = step.config;

  switch (step.functionName) {
    case 'UPPER':
      return `UPPER(${input})`;
    case 'LOWER':
      return `LOWER(${input})`;
    case 'TRIM':
      return `TRIM(${input})`;
    case 'ABS':
      return `ABS(${input})`;

    case 'ROUND':
      return `ROUND(${input}, ${c.decimals ?? 2})`;

    case 'CAST':
      return `CAST(${input} AS ${c.targetType ?? 'INT'})`;

    case 'DATE_FORMAT':
      return `DATE_FORMAT(${input}, '${c.format ?? '%Y-%m-%d'}')`;

    case 'PARSE_DATE':
      return `PARSE_DATE(${input}, '${c.format ?? '%Y-%m-%d'}')`;

    case 'SPLIT':
      return `SPLIT(${input}, '${c.delimiter ?? ','}')${c.index !== undefined ? `[${c.index}]` : '[0]'}`;

    case 'REPLACE':
      return `REPLACE(${input}, '${c.find ?? ''}', '${c.replaceWith ?? ''}')`;

    case 'CONCAT': {
      const parts = (c.parts ?? []).map((p) => configValueToString(p, sourceFields));
      return `CONCAT(${parts.join(', ')})`;
    }

    case 'COALESCE': {
      const fallbacks = (c.fallbacks ?? []).map((f) => configValueToString(f, sourceFields));
      return `COALESCE(${fallbacks.join(', ')})`;
    }

    case 'IF': {
      const field = c.conditionField ? configValueToRaw(c.conditionField, sourceFields) : input;
      const op = c.operator ?? '=';
      const condVal = c.conditionValue ?? '';
      const trueVal = c.trueValue ? configValueToString(c.trueValue, sourceFields) : "'true'";
      const falseVal = c.falseValue ? configValueToString(c.falseValue, sourceFields) : "'false'";

      if (op === 'IS NULL') {
        return `IF(${field} IS NULL, ${trueVal}, ${falseVal})`;
      }
      if (op === 'IS NOT NULL') {
        return `IF(${field} IS NOT NULL, ${trueVal}, ${falseVal})`;
      }

      const quotedVal = condVal.startsWith("'") ? condVal : `'${condVal}'`;
      return `IF(${field} ${op} ${quotedVal}, ${trueVal}, ${falseVal})`;
    }

    case 'TO_DATE':
      if (c.format) return `TO_DATE(${input}, '${c.format}')`;
      return `TO_DATE(${input})`;
    case 'TO_CHAR':
      if (c.format) return `TO_CHAR(${input}, '${c.format}')`;
      return `TO_CHAR(${input})`;
    case 'TO_TIMESTAMP':
      if (c.format) return `TO_TIMESTAMP(${input}, '${c.format}')`;
      return `TO_TIMESTAMP(${input})`;
    case 'IS_NULL':
      return `IS_NULL(${input})`;
    case 'IS_NOT_NULL':
      return `IS_NOT_NULL(${input})`;
    case 'LENGTH':
      return `LENGTH(${input})`;
    case 'SUBSTRING':
      return `SUBSTRING(${input}, ${c.start ?? 1}, ${c.length ?? 10})`;
    case 'LEFT':
      return `LEFT(${input}, ${c.length ?? 5})`;
    case 'RIGHT':
      return `RIGHT(${input}, ${c.length ?? 5})`;
    case 'LPAD':
      return `LPAD(${input}, ${c.padLength ?? 10}, '${c.padChar ?? '0'}')`;
    case 'RPAD':
      return `RPAD(${input}, ${c.padLength ?? 10}, '${c.padChar ?? ' '}')`;

    case 'NVL': {
      const fb = (c.fallbacks ?? []).map((f) => configValueToString(f, sourceFields));
      return `NVL(${input}, ${fb.join(', ')})`;
    }
    case 'LITERAL':
      return `LITERAL('${(c.literalValue ?? '').replace(/'/g, "\\'")}')`;
    case 'CASE_WHEN': {
      const branches = c.branches ?? [];
      let result = `CASE`;
      for (const b of branches) {
        result += ` WHEN ${input} = '${b.condition}' THEN '${b.result}'`;
      }
      if (c.elseResult !== undefined && c.elseResult !== '') {
        result += ` ELSE '${c.elseResult}'`;
      }
      result += ' END';
      return result;
    }

    default:
      // Unknown function — best effort
      return `${step.functionName}(${input})`;
  }
};

// ---------------------------------------------------------------------------
// Parse: expression string → VisualPipeline (best-effort)
// ---------------------------------------------------------------------------

interface ParseResult {
  steps: PipelineStep[];
  baseField: string;
}

let _stepIdCounter = 0;
const nextStepId = (): string => `step-${++_stepIdCounter}`;

/** Reset step counter (useful between calls) */
export const resetStepIdCounter = (): void => {
  _stepIdCounter = 0;
};

/**
 * Walk all ConfigValues in parsed steps and fix fieldIds to match the real
 * source-field IDs (which include the source file prefix).  The parser creates
 * temporary IDs like "sf-SUBJECT" but extractSourceFields produces
 * "sf-edc-visit-schedule-SUBJECT".
 */
const reconcileFieldIds = (
  steps: PipelineStep[],
  availableSourceFields: PipelineSourceField[],
): PipelineStep[] => {
  if (availableSourceFields.length === 0) return steps;

  const fixValue = (cv: ConfigValue): ConfigValue => {
    if (cv.kind !== 'field') return cv;
    // Already matches an available field — nothing to do
    if (availableSourceFields.some((sf) => sf.id === cv.fieldId)) return cv;
    // Try to match by field name (case-insensitive)
    const match = availableSourceFields.find(
      (sf) =>
        sf.sourceField === cv.fieldName ||
        sf.sourceField.toLowerCase() === cv.fieldName.toLowerCase(),
    );
    if (match) {
      return { kind: 'field', fieldId: match.id, fieldName: match.sourceField };
    }
    return cv;
  };

  return steps.map((step) => {
    const config = { ...step.config };
    if (config.parts) config.parts = config.parts.map(fixValue);
    if (config.fallbacks) config.fallbacks = config.fallbacks.map(fixValue);
    if (config.conditionField) config.conditionField = fixValue(config.conditionField);
    if (config.trueValue) config.trueValue = fixValue(config.trueValue);
    if (config.falseValue) config.falseValue = fixValue(config.falseValue);
    return { ...step, config };
  });
};

/**
 * Attempt to parse an expression string into a VisualPipeline.
 * Returns isAdvancedMode: true if parsing fails.
 */
export const parsePipelineFromExpression = (
  expression: string,
  availableSourceFields: PipelineSourceField[]
): VisualPipeline => {
  _stepIdCounter = 0;

  const trimmed = (expression ?? '').trim();
  if (!trimmed) {
    return {
      sourceFields: availableSourceFields.length > 0 ? [availableSourceFields[0]] : [],
      steps: [],
      isAdvancedMode: false,
    };
  }

  try {
    const result = parseExpression(trimmed);
    if (!result) {
      return fallback(expression, availableSourceFields);
    }

    // Try to find the base field in available source fields
    const baseFieldName = result.baseField;
    const matchedSourceField = availableSourceFields.find(
      (sf) =>
        sf.sourceField === baseFieldName ||
        sf.qualifiedName === baseFieldName ||
        sf.sourceField.toLowerCase() === baseFieldName.toLowerCase()
    );

    const sourceFields = matchedSourceField
      ? [matchedSourceField]
      : availableSourceFields.length > 0
      ? [availableSourceFields[0]]
      : [{
          id: 'sf-parsed',
          sourceFile: '',
          sourceField: baseFieldName,
          qualifiedName: baseFieldName,
        }];

    // Reconcile field references in step configs: the parser creates temporary
    // fieldIds like "sf-SUBJECT" but the real IDs include the source file prefix
    // (e.g. "sf-edc-visit-schedule-SUBJECT"). Walk all ConfigValues and fix them.
    const reconciledSteps = reconcileFieldIds(result.steps, availableSourceFields);

    return {
      sourceFields,
      steps: reconciledSteps,
      isAdvancedMode: false,
    };
  } catch {
    return fallback(expression, availableSourceFields);
  }
};

const fallback = (expression: string, availableSourceFields: PipelineSourceField[]): VisualPipeline => ({
  sourceFields: availableSourceFields.length > 0 ? [availableSourceFields[0]] : [],
  steps: [],
  isAdvancedMode: true,
});

// ---------------------------------------------------------------------------
// Recursive descent parser for nested function calls
// ---------------------------------------------------------------------------

/**
 * Match the outermost function call: FUNC_NAME(args)
 * Returns null if the expression is just a plain identifier.
 */
const parseExpression = (expr: string): ParseResult | null => {
  const trimmed = expr.trim();

  // Check for SPLIT with index: SPLIT(...)[N]
  const splitIndexMatch = trimmed.match(/^SPLIT\s*\(([\s\S]+)\)\s*\[(\d+)\]$/i);
  if (splitIndexMatch) {
    const innerArgs = splitIndexMatch[1];
    const index = parseInt(splitIndexMatch[2], 10);
    return parseSplitCall(innerArgs, index);
  }

  // Check for CASE WHEN ... END
  if (trimmed.toUpperCase().startsWith('CASE') && trimmed.toUpperCase().endsWith('END')) {
    return parseCaseWhen(trimmed);
  }

  // Check for standard FUNC(args) pattern
  const funcMatch = trimmed.match(/^([A-Z_]+)\s*\(([\s\S]+)\)$/i);
  if (!funcMatch) {
    // Plain identifier (base field) — strip surrounding double-quotes if present
    return { steps: [], baseField: unquoteIdentifier(trimmed) };
  }

  const funcName = funcMatch[1].toUpperCase();
  const argsStr = funcMatch[2];

  // Special handling for CAST: CAST(expr AS TYPE)
  if (funcName === 'CAST') {
    return parseCastCall(argsStr);
  }

  // Simple unary functions
  if (['UPPER', 'LOWER', 'TRIM', 'ABS', 'IS_NULL', 'IS_NOT_NULL', 'LENGTH'].includes(funcName)) {
    const inner = parseExpression(argsStr);
    if (!inner) return null;
    const step: PipelineStep = {
      id: nextStepId(),
      functionName: funcName,
      config: {},
    };
    return { steps: [...inner.steps, step], baseField: inner.baseField };
  }

  // ROUND(expr, N)
  if (funcName === 'ROUND') {
    return parseRoundCall(argsStr);
  }

  // DATE_FORMAT / PARSE_DATE
  if (funcName === 'DATE_FORMAT' || funcName === 'PARSE_DATE') {
    return parseDateFuncCall(funcName, argsStr);
  }

  // REPLACE(expr, "find", "replace")
  if (funcName === 'REPLACE') {
    return parseReplaceCall(argsStr);
  }

  // CONCAT(a, b, ...)
  if (funcName === 'CONCAT') {
    return parseConcatCall(argsStr);
  }

  // COALESCE(a, b, ...)
  if (funcName === 'COALESCE') {
    return parseCoalesceCall(argsStr);
  }

  // TO_DATE / TO_CHAR / TO_TIMESTAMP - may have 1 or 2 args
  if (funcName === 'TO_DATE' || funcName === 'TO_CHAR' || funcName === 'TO_TIMESTAMP') {
    return parseDateConversionCall(funcName, argsStr);
  }

  // LITERAL('value')
  if (funcName === 'LITERAL') {
    return parseLiteralCall(argsStr);
  }

  // NVL(expr, default)
  if (funcName === 'NVL') {
    return parseNvlCall(argsStr);
  }

  // SUBSTRING(expr, start, length)
  if (funcName === 'SUBSTRING' || funcName === 'SUBSTR') {
    return parseSubstringCall(argsStr);
  }

  // LEFT(expr, n) / RIGHT(expr, n)
  if (funcName === 'LEFT' || funcName === 'RIGHT') {
    return parseLeftRightCall(funcName, argsStr);
  }

  // LPAD(expr, n, char) / RPAD(expr, n, char)
  if (funcName === 'LPAD' || funcName === 'RPAD') {
    return parsePadCall(funcName, argsStr);
  }

  // IF(cond, true, false)
  if (funcName === 'IF') {
    return parseIfCall(argsStr);
  }

  // Unknown function — cannot parse visually
  return null;
};

/** Split a comma-separated argument list respecting nested parentheses and quotes */
const splitArgs = (argsStr: string): string[] => {
  const args: string[] = [];
  let depth = 0;
  let inQuote = false;
  let quoteChar = '';
  let current = '';

  for (let i = 0; i < argsStr.length; i++) {
    const ch = argsStr[i];

    if (inQuote) {
      current += ch;
      if (ch === quoteChar && argsStr[i - 1] !== '\\') {
        inQuote = false;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      inQuote = true;
      quoteChar = ch;
      current += ch;
      continue;
    }

    if (ch === '(') {
      depth++;
      current += ch;
      continue;
    }
    if (ch === ')') {
      depth--;
      current += ch;
      continue;
    }

    if (ch === ',' && depth === 0) {
      args.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }
  if (current.trim()) {
    args.push(current.trim());
  }
  return args;
};

const parseStringLiteral = (value: string): string | null => {
  const trimmed = value.trim();
  const m = trimmed.match(/^"((?:[^"\\]|\\.)*)"$/);
  if (m) return m[1].replace(/\\"/g, '"');
  const m2 = trimmed.match(/^'((?:[^'\\]|\\.)*)'$/);
  if (m2) return m2[1].replace(/\\'/g, "'");
  return null;
};

const argToConfigValue = (arg: string): ConfigValue => {
  const trimmed = arg.trim();
  // Double-quoted strings are identifiers (field references), not literals
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    const fieldName = unquoteIdentifier(trimmed);
    return { kind: 'field', fieldId: `sf-${fieldName}`, fieldName };
  }
  const literal = parseStringLiteral(arg);
  if (literal !== null) {
    return { kind: 'literal', value: literal };
  }
  // Treat as a field reference
  return { kind: 'field', fieldId: `sf-${arg}`, fieldName: arg };
};

// --- Per-function parsers ---

const parseCastCall = (argsStr: string): ParseResult | null => {
  const m = argsStr.match(/^([\s\S]+?)\s+AS\s+(\w+)$/i);
  if (!m) return null;
  const inner = parseExpression(m[1].trim());
  if (!inner) return null;
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'CAST',
    config: { targetType: m[2].toUpperCase() },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseRoundCall = (argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const decimals = parseInt(args[1], 10);
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'ROUND',
    config: { decimals: Number.isNaN(decimals) ? 2 : decimals },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseDateFuncCall = (funcName: string, argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const format = parseStringLiteral(args[1]) ?? args[1];
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: funcName,
    config: { format },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseSplitCall = (argsStr: string, index: number): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const delimiter = parseStringLiteral(args[1]) ?? args[1];
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'SPLIT',
    config: { delimiter, index },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseReplaceCall = (argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 3) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const find = parseStringLiteral(args[1]) ?? args[1];
  const replaceWith = parseStringLiteral(args[2]) ?? args[2];
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'REPLACE',
    config: { find, replaceWith },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseConcatCall = (argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const parts: ConfigValue[] = args.map(argToConfigValue);
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'CONCAT',
    config: { parts },
  };
  // CONCAT replaces the pipeline input — use first field arg as base if available
  const firstFieldPart = parts.find((p) => p.kind === 'field');
  const baseField = firstFieldPart && firstFieldPart.kind === 'field' ? firstFieldPart.fieldName : args[0];
  return { steps: [step], baseField };
};

const parseCoalesceCall = (argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const fallbacks: ConfigValue[] = args.map(argToConfigValue);
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'COALESCE',
    config: { fallbacks },
  };
  const firstFieldPart = fallbacks.find((p) => p.kind === 'field');
  const baseField = firstFieldPart && firstFieldPart.kind === 'field' ? firstFieldPart.fieldName : args[0];
  return { steps: [step], baseField };
};

const parseIfCall = (argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 3) return null;

  // args[0] is the condition expression, args[1] is true val, args[2] is false val
  const condExpr = args[0].trim();
  const trueValue = argToConfigValue(args[1]);
  const falseValue = argToConfigValue(args[2]);

  // Try to parse condition: field OP value (field may be double-quoted)
  const condMatch = condExpr.match(/^("(?:[^"]+)"|\w[\w.]*)\s+(IS\s+NOT\s+NULL|IS\s+NULL|[!=<>]+|LIKE)\s+([\s\S]*)$/i);
  let conditionField: ConfigValue;
  let operator: string;
  let conditionValue: string;

  if (condMatch) {
    const rawField = unquoteIdentifier(condMatch[1]);
    conditionField = { kind: 'field', fieldId: `sf-${rawField}`, fieldName: rawField };
    operator = condMatch[2].toUpperCase().replace(/\s+/g, ' ');
    conditionValue = parseStringLiteral(condMatch[3]) ?? condMatch[3];
  } else {
    // Can't parse condition — put the whole thing as literal
    conditionField = { kind: 'literal', value: condExpr };
    operator = '=';
    conditionValue = '';
  }

  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'IF',
    config: { conditionField, operator, conditionValue, trueValue, falseValue },
  };

  const baseField = conditionField.kind === 'field' ? conditionField.fieldName : '';
  return { steps: [step], baseField };
};

const parseDateConversionCall = (funcName: string, argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length === 0) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const format = args.length >= 2 ? (parseStringLiteral(args[1]) ?? args[1]) : '';
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: funcName,
    config: format ? { format } : {},
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseLiteralCall = (argsStr: string): ParseResult | null => {
  const literal = parseStringLiteral(argsStr.trim());
  const value = literal !== null ? literal : argsStr.trim();
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'LITERAL',
    config: { literalValue: value },
  };
  // LITERAL has no real base field - use empty
  return { steps: [step], baseField: '' };
};

const parseNvlCall = (argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const fallbacks: ConfigValue[] = args.slice(1).map(argToConfigValue);
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'NVL',
    config: { fallbacks },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseSubstringCall = (argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const start = parseInt(args[1], 10) || 1;
  const length = args.length >= 3 ? parseInt(args[2], 10) || 10 : undefined;
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'SUBSTRING',
    config: { start, length },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseLeftRightCall = (funcName: string, argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const length = parseInt(args[1], 10) || 5;
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: funcName,
    config: { length },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parsePadCall = (funcName: string, argsStr: string): ParseResult | null => {
  const args = splitArgs(argsStr);
  if (args.length < 2) return null;
  const inner = parseExpression(args[0]);
  if (!inner) return null;
  const padLength = parseInt(args[1], 10) || 10;
  const padChar = args.length >= 3 ? (parseStringLiteral(args[2]) ?? args[2]) : (funcName === 'LPAD' ? '0' : ' ');
  const step: PipelineStep = {
    id: nextStepId(),
    functionName: funcName,
    config: { padLength, padChar },
  };
  return { steps: [...inner.steps, step], baseField: inner.baseField };
};

const parseCaseWhen = (expr: string): ParseResult | null => {
  // Parse: CASE WHEN field = 'val' THEN 'result' WHEN ... ELSE 'default' END
  const inner = expr.replace(/^CASE\s+/i, '').replace(/\s+END$/i, '').trim();

  const branches: Array<{ condition: string; result: string }> = [];
  let elseResult = '';
  let baseField = '';

  // Extract ELSE clause first
  const elseMatch = inner.match(/\bELSE\s+([\s\S]+)$/i);
  let whenPart = inner;
  if (elseMatch) {
    const elseVal = elseMatch[1].trim();
    elseResult = parseStringLiteral(elseVal) ?? elseVal;
    // Handle TRUE/FALSE literals
    if (elseVal.toUpperCase() === 'TRUE') elseResult = 'TRUE';
    if (elseVal.toUpperCase() === 'FALSE') elseResult = 'FALSE';
    whenPart = inner.substring(0, elseMatch.index).trim();
  }

  // Parse WHEN clauses: WHEN field = 'value' THEN 'result' (field may be double-quoted)
  const whenRegex = /WHEN\s+("(?:[^"]+)"|[\w.]+)\s*=\s*'([^']*)'\s+THEN\s+('([^']*)'|TRUE|FALSE)/gi;
  let match;
  while ((match = whenRegex.exec(whenPart)) !== null) {
    if (!baseField) baseField = unquoteIdentifier(match[1]);
    const condition = match[2];
    const result = match[4] !== undefined ? match[4] : match[3]; // match[3] for TRUE/FALSE, match[4] for quoted
    branches.push({ condition, result });
  }

  if (branches.length === 0) return null;

  const step: PipelineStep = {
    id: nextStepId(),
    functionName: 'CASE_WHEN',
    config: { branches, elseResult },
  };
  return { steps: [step], baseField };
};
